import { useEffect } from "react"

/**
 * Stops a file dropped BESIDE a drop zone from taking the browser out of the application.
 *
 * In the HTML drag-and-drop specification an element refuses drops by default, and accepts one by
 * calling `preventDefault()` on `dragover`. Our drop zones do that on their own element; everywhere
 * else nobody does, so no `drop` event ever reaches the application and the browser falls back to
 * handling the file itself — for an XML that means replacing the page with the XML tree, and every
 * context of this single-page application lives in memory, so the user loses whatever they had in
 * progress.
 *
 * Missing the zone by a few pixels is enough. The home page invites the gesture on every visit,
 * which is what turns a rare accident into a likely one.
 */
export function refuseStrayDrag(event: DragEvent): void {
  // A real drop zone already accepted this one — its own handler ran first, since events travel
  // from the target up to the window. Leave it alone.
  if (event.defaultPrevented) return

  // Accept the event only to neutralise it: without this the browser navigates to the file.
  event.preventDefault()
  // And say so honestly. `preventDefault` alone makes the pointer promise a drop that would do
  // nothing at all; "none" shows the refusal instead.
  if (event.dataTransfer) event.dataTransfer.dropEffect = "none"
}

/** Wires {@link refuseStrayDrag} for the whole application. Mounted once, at the root. */
export function useStrayFileDropGuard(): void {
  useEffect(() => {
    window.addEventListener("dragover", refuseStrayDrag)
    window.addEventListener("drop", refuseStrayDrag)
    return () => {
      window.removeEventListener("dragover", refuseStrayDrag)
      window.removeEventListener("drop", refuseStrayDrag)
    }
  }, [])
}
